import { callEDataVoiceApi } from "./EDVApiClient"
import { Campagne, Occurrence, RedistributionEntry, Target, TargetDistribution } from "../types/MarketingCampaign"

interface CampaignResponse
{
    data: Campagne
}

interface TargetsDistributionResponse
{
    data: TargetDistribution[]
}

interface TargetsResponse
{
    data: Target[];
}

interface OccurrencesResponse
{
    data: Occurrence[];
}

interface OccurrenceResponse
{
    data: Occurrence;
}

export async function getCampaign(id: number): Promise<Campagne> {
    const response = await callEDataVoiceApi<CampaignResponse>(
        "marketing-campaigns/" + id + "?include=dealerships,occurrences")
    return response.data
}

export async function getTargetsDistribution(id: number, selectedOccurrence: number, dealershipId: number): Promise<TargetDistribution[]> {
    const response = await callEDataVoiceApi<TargetsDistributionResponse>(
        "marketing-campaigns/" + id + "/occurrences/" + selectedOccurrence + "/targets/distribution?filter[dealership.id]=" + dealershipId)

    return response.data || []
}

export async function getTargets(id: number, selectedOccurrence: number): Promise<Target[]> {
    const response = await callEDataVoiceApi<TargetsResponse>(
        "marketing-campaigns/" + id + "/targets" + (selectedOccurrence ? "?filter[occurrence_id]=" + selectedOccurrence : ""))

    return response.data || []
}

// dealership_id est nécessaire pour obtenir user_targets_count (le périmètre de l'utilisateur) ;
// targets_count reste, lui, la cible de l'occurrence entière, tous sites confondus.
export async function getOccurrence(id: number, selectedOccurrence: number, dealershipId: number): Promise<Occurrence> {
    const response = await callEDataVoiceApi<OccurrenceResponse>(
        "marketing-campaigns/" + id + "/occurrences/" + selectedOccurrence + "?dealership_id=" + dealershipId)
    return response.data
}

export async function getOccurrences(id: number): Promise<Occurrence[]> {
    const response = await callEDataVoiceApi<OccurrencesResponse>(
        "marketing-campaigns/" + id + "/occurrences" )
    return response.data || []
}

export async function targetAssignment(id: number, selectedOccurrence: number, targetsDistribution: TargetDistribution[], targetDistributionByUser: RedistributionEntry[], dealershipId: number) {
    // Un commercial peut à la fois avoir une affectation saisie à la main et une part du solde
    // redistribué : on fusionne les deux sources par user_id pour ne pas envoyer deux
    // assignations pour le même utilisateur.
    const assignmentsByUser = new Map<number, { user_id: number, mass_assignment_count: number, target_ids: number[] }>()

    for (const d of [ ...targetsDistribution, ...targetDistributionByUser ]) {
        const userId = d.followed_by.id
        if (!userId) {
            continue
        }

        const assignment = assignmentsByUser.get(userId) || {
            user_id: userId,
            mass_assignment_count: 0,
            target_ids: [],
        }

        assignment.mass_assignment_count += ("selected_targets" in d ? d.selected_targets : 0) || 0
        assignment.target_ids = [ ...assignment.target_ids, ...("target_ids" in d ? d.target_ids : []) ]

        assignmentsByUser.set(userId, assignment)
    }

    return await callEDataVoiceApi<any>(
        "marketing-campaigns/" + id + "/occurrences/" + selectedOccurrence + "/targets-assignment",
        {
            method: "POST",
            body: JSON.stringify({
                "dealership_ids": [
                    dealershipId,
                ],
                "assignments": Array.from(assignmentsByUser.values()),
            }),
        },
    )
}

export async function getUnfollowedTargets(marketingCampaignId: number, dealershipId: number, occurrenceId: number): Promise<Target[]> {
    const response = await callEDataVoiceApi<any>(
        "marketing-campaigns/" + marketingCampaignId + "/targets?filter[followed_by_user_id]=0&filter[dealership.id]=" + dealershipId + "&filter[occurrence.id]=" + occurrenceId,
    );
    if (response && Array.isArray(response.data)) {
        return response.data;
    }
    return [];
}

// Répartit équitablement un nombre total de cibles (contacts non affectés) entre plusieurs commerciaux, 
// en tenant compte des assignements existants (cibles déjà suivies et assignées).
// Utilisé à la fois pour l'aperçu dans la modale de répartition et pour l'attribution réelle, afin
// que les deux calculs restent identiques.
export const computeEquitableDistribution = (sellers: TargetDistribution[], total: number): number[] => {
    if (sellers.length === 0) {
        return []
    }

    const workloads: number[] = sellers.map((seller: TargetDistribution) => seller.followed_with_assignment_count + (seller.selected_targets || 0))
    const distribution: number[] = sellers.map(() => 0)

    // Distribue nombre total de cibles une par une : à chaque tour, on cherche le commercial le
    // moins chargé (workload le plus faible) et on lui attribue la cible suivante
    for (let i = 0; i < total; i++) {
        let lightestIndex = 0
        for (let j = 1; j < workloads.length; j++) {
            if (workloads[j] < workloads[lightestIndex]) {
                lightestIndex = j
            }
        }
        workloads[lightestIndex]++
        distribution[lightestIndex]++
    }

    return distribution
}
